import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { ChevronDownIcon, InstagramIcon, LinkedinIcon, MenuIcon, XIcon, YoutubeIcon } from 'lucide-react';
import { Logo } from '../shell/Logo';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Public navigation.
 *
 * Six destinations, each either a direct route or a small menu of real routes.
 * Nothing here is a placeholder: every link resolves, and a menu only exists
 * where there is genuinely more than one place to go.
 */
interface NavLinkItem {
  to: string;
  label: string;
  note?: string;
}
interface NavEntry {
  label: string;
  to?: string;
  end?: boolean;
  items?: NavLinkItem[];
}
const PRIMARY: NavEntry[] = [{
  label: 'Product',
  items: [{
    to: '/academics',
    label: 'Academics',
    note: 'Courses, results, timetable'
  }, {
    to: '/housing',
    label: 'Housing',
    note: 'Find and apply for a place'
  }, {
    to: '/market',
    label: 'Marketplace',
    note: 'Food, books, services, gigs'
  }, {
    to: '/money',
    label: 'Money',
    note: 'Fees, payments and records'
  }, {
    to: '/social',
    label: 'Campus',
    note: 'Events, communities, clubs'
  }]
}, {
  label: 'Solutions',
  items: [{
    to: '/welcome/campuses',
    label: 'For institutions',
    note: 'Campus digital infrastructure'
  }, {
    to: '/market/sell',
    label: 'For providers',
    note: 'Sell products or offer services'
  }, {
    to: '/network',
    label: 'For representatives',
    note: 'Authorised field operators'
  }]
}, {
  label: 'For students',
  items: [{
    to: '/register',
    label: 'Create your account'
  }, {
    to: '/discover',
    label: 'Discover universities and programmes'
  }, {
    to: '/opportunities',
    label: 'Jobs and opportunities'
  }, {
    to: '/services',
    label: 'Academic services'
  }]
}, {
  label: 'For campuses',
  to: '/welcome/campuses'
}, {
  label: 'Marketplace',
  to: '/market'
}, {
  label: 'Resources',
  items: [{
    to: '/support',
    label: 'Help centre'
  }, {
    to: '/legal/privacy',
    label: 'Privacy'
  }, {
    to: '/legal/terms',
    label: 'Terms'
  }]
}];
const FOOTER_COLUMNS = [{
  heading: 'Platform',
  links: [{
    to: '/academics',
    label: 'Academics'
  }, {
    to: '/social',
    label: 'Campus'
  }, {
    to: '/market',
    label: 'Marketplace'
  }, {
    to: '/housing',
    label: 'Housing'
  }, {
    to: '/money',
    label: 'Money'
  }, {
    to: '/services',
    label: 'Services'
  }, {
    to: '/opportunities',
    label: 'Opportunities'
  }]
}, {
  heading: 'For campuses',
  links: [{
    to: '/welcome/campuses',
    label: 'Campus platform'
  }, {
    to: '/institution',
    label: 'Administration'
  }, {
    to: '/institution',
    label: 'Analytics'
  }, {
    to: '/notifications',
    label: 'Communication'
  }, {
    to: '/admin/health',
    label: 'Integration'
  }, {
    to: '/support',
    label: 'Support'
  }]
}, {
  heading: 'For providers',
  links: [{
    to: '/market/sell',
    label: 'Become a provider'
  }, {
    to: '/provider',
    label: 'Provider console'
  }, {
    to: '/identity',
    label: 'Verification'
  }, {
    to: '/network',
    label: 'Representative network'
  }]
}, {
  heading: 'Resources',
  links: [{
    to: '/support',
    label: 'Help centre'
  }, {
    to: '/support',
    label: 'Safety'
  }, {
    to: '/welcome/campuses',
    label: 'Campuses'
  }, {
    to: '/notifications',
    label: 'Updates'
  }]
}, {
  heading: 'Company',
  links: [{
    to: '/welcome',
    label: 'About'
  }, {
    to: '/support',
    label: 'Contact'
  }, {
    to: '/network',
    label: 'Careers'
  }, {
    to: '/legal/privacy',
    label: 'Privacy'
  }, {
    to: '/legal/terms',
    label: 'Terms'
  }]
}];
const SOCIAL = [{
  label: 'Instagram',
  icon: InstagramIcon
}, {
  label: 'LinkedIn',
  icon: LinkedinIcon
}, {
  label: 'YouTube',
  icon: YoutubeIcon
}];
function NavMenu({
  entry
}: {
  entry: NavEntry;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!open) return;
    const onAway = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <li ref={ref} className="relative shrink-0" onMouseLeave={() => setOpen(false)} data-id="element-4374" data-mp="2pqig9jh3styr">
      <button type="button" onClick={() => setOpen(current => !current)} onMouseEnter={() => setOpen(true)} aria-expanded={open} className="flex min-h-touch items-center gap-1 px-3 text-body-sm font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-4375" data-mp="2trc4kdygkas6">
        {entry.label}
        <ChevronDownIcon aria-hidden className={`h-4 w-4 transition-transform duration-150 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-4376" data-mp="2cit2b2naapdy" />
      </button>

      {open && <div className="absolute left-0 top-full z-40 w-72 rounded-card border border-line bg-surface p-2 shadow-[0_16px_40px_rgba(15,23,42,0.12)]" data-id="element-4377" data-mp="2m9nraerx0333">
          <ul data-id="element-4378" data-mp="2w60jt9bq4aw9">
            {entry.items?.map(item => <li key={`${entry.label}-${item.label}`} data-id="element-4379" data-mp="2w4fp9jpesrye">
                <Link to={item.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-4380" data-mp="2r7z14pi7mdlb">
                  <span className="block text-body-sm font-semibold text-ink" data-id="element-4381" data-mp="308dnztato0qx">{item.label}</span>
                  {item.note && <span className="mt-0.5 block text-caption text-ink-muted" data-id="element-4382" data-mp="15tulvru6zz9f">{item.note}</span>}
                </Link>
              </li>)}
          </ul>
        </div>}
    </li>;
}

/**
 * The public chrome. Used as a layout route (rendering an Outlet) and, for the
 * homepage, with an explicit child so the experience controller can present it
 * without a route change. Passing children changes nothing else about it.
 */
export function PublicShell({
  children
}: {
  children?: React.ReactNode;
}) {
  const {
    authenticated,
    identity
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="min-h-screen w-full bg-surface" data-id="element-4383" data-mp="bgk4130q2ngr">
      <a href="#public-main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-ink focus:px-4 focus:py-2 focus:text-body-sm focus:text-white" data-id="element-4384" data-mp="bdz1ebslo3hy">
        Skip to content
      </a>

      <header className={`sticky top-0 z-30 bg-surface/95 backdrop-blur transition-shadow duration-200 ease-out ${scrolled ? 'shadow-[0_1px_0_rgba(15,23,42,0.08),0_8px_24px_rgba(15,23,42,0.06)]' : ''}`} data-id="element-4385" data-mp="1vx1ivmz2u5dm">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6" data-id="element-4386" data-mp="1mzha7syvx6sz">
          <Link to="/welcome" className="shrink-0 rounded-md" data-id="element-4387" data-mp="2jmvphmssyxpf">
            <Logo data-id="element-4388" data-mp="2fn4sh1conlcg" />
          </Link>

          <nav aria-label="Campuzon" className="hidden min-w-0 flex-1 justify-center lg:flex" data-id="element-4389" data-mp="2fo6hk9qja8d6">
            <ul className="flex items-center gap-0.5" data-id="element-4390" data-mp="2atit2avj5dyl">
              {PRIMARY.map(entry => entry.items ? <NavMenu key={entry.label} entry={entry} data-id="element-4391" data-mp="33bo4nryusi9t" /> : <li key={entry.label} className="shrink-0" data-id="element-4392" data-mp="33dhax65k10ha">
                    <NavLink to={entry.to as string} end={entry.end} className={({
                isActive
              }) => `flex min-h-touch items-center px-3 text-body-sm font-medium transition-colors duration-150 ease-out ${isActive ? 'text-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-4393" data-mp="2yk62j0l3gbn9">
                      {entry.label}
                    </NavLink>
                  </li>)}
            </ul>
          </nav>

          <div className="ml-auto hidden items-center gap-3 lg:flex" data-id="element-4394" data-mp="2ulmjrr9dzi8a">
            {authenticated ? <Link to="/" data-id="element-4395" data-mp="2gmmt6e2bct9h">
                <Button variant="primary" data-id="element-4396" data-mp="2gpiqq1a91fxe">
                  {identity ? `Continue as @${identity.username}` : 'Go to Campuzon'}
                </Button>
              </Link> : <>
                <Link to="/signin" className="flex min-h-touch items-center px-2 text-body-sm font-medium text-ink transition-colors duration-150 ease-out hover:text-brand-700" data-id="element-4397" data-mp="199ment9v2z54">
                  Sign in
                </Link>
                <Link to="/register" data-id="element-4398" data-mp="wt36hts2tcfk">
                  <Button variant="primary" data-id="element-4399" data-mp="1bosws44aobq3">Get started</Button>
                </Link>
              </>}
          </div>

          <button type="button" onClick={() => setOpen(current => !current)} className="ml-auto flex min-h-touch min-w-touch items-center justify-center rounded-md text-ink lg:hidden" aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'} data-id="element-4400" data-mp="16qhnpvltq58x">
            {open ? <XIcon aria-hidden className="h-6 w-6" data-id="element-4401" data-mp="nyorztfde5xi" /> : <MenuIcon aria-hidden className="h-6 w-6" data-id="element-4402" data-mp="nwlu6n7z78i8" />}
          </button>
        </div>

        {open && <div className="max-h-[80dvh] overflow-y-auto border-t border-line bg-surface lg:hidden" data-id="element-4403" data-mp="3rff50s9ddj8p">
            <nav aria-label="Campuzon" className="mx-auto max-w-7xl px-4 py-3 sm:px-6" data-id="element-4404" data-mp="l3nlzsuoto8a">
              <ul className="space-y-4" data-id="element-4405" data-mp="l1b1b7xjozrj">
                {PRIMARY.map(entry => <li key={entry.label} data-id="element-4406" data-mp="gzxuovmmosjk">
                    {entry.items ? <>
                        <p className="px-1 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4407" data-mp="c4sf0ejtka3x">
                          {entry.label}
                        </p>
                        <ul className="mt-1 divide-y divide-line" data-id="element-4408" data-mp="262u0cwmm9jg">
                          {entry.items.map(item => <li key={`m-${entry.label}-${item.label}`} data-id="element-4409" data-mp="3phvg9vqlh92h">
                              <Link to={item.to} onClick={() => setOpen(false)} className="flex min-h-touch-lg items-center px-1 text-body text-ink" data-id="element-4410" data-mp="3pi3sdlew8acm">
                                {item.label}
                              </Link>
                            </li>)}
                        </ul>
                      </> : <Link to={entry.to as string} onClick={() => setOpen(false)} className="flex min-h-touch-lg items-center px-1 text-body font-semibold text-ink" data-id="element-4411" data-mp="35dwkcx0bsqbw">
                        {entry.label}
                      </Link>}
                  </li>)}
              </ul>

              <div className="flex flex-col gap-2 py-4" data-id="element-4412" data-mp="2mn5fa7ndfm3b">
                {authenticated ? <Link to="/" onClick={() => setOpen(false)} data-id="element-4413" data-mp="2rm9lqqiwv5py">
                    <Button variant="primary" block data-id="element-4414" data-mp="2wg4cuf0uimz2">
                      Go to Campuzon
                    </Button>
                  </Link> : <>
                    <Link to="/register" onClick={() => setOpen(false)} data-id="element-4415" data-mp="23xypk0o5tu80">
                      <Button variant="primary" block data-id="element-4416" data-mp="1vy2fn220e9z0">
                        Get started
                      </Button>
                    </Link>
                    <Link to="/signin" onClick={() => setOpen(false)} data-id="element-4417" data-mp="1qy7p4zm5bmeh">
                      <Button variant="secondary" block data-id="element-4418" data-mp="25o59t5kv9tyx">
                        Sign in
                      </Button>
                    </Link>
                  </>}
              </div>
            </nav>
          </div>}
      </header>

      <main id="public-main" data-id="element-4419" data-mp="1d11c7lo3shx6">{children ?? <Outlet data-id="element-4420" data-mp="180mzvrfgyltc" />}</main>

      {/* ------------------------------------------------------------ footer */}
      <footer className="bg-ink" data-id="element-4421" data-mp="1mxnuc2p1ouvi">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-14" data-id="element-4422" data-mp="1myp7qdtj9tge">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,3.4fr)]" data-id="element-4423" data-mp="1hx9kcf3vvauu">
            <div data-id="element-4424" data-mp="2ybrphc7dcp25">
              {/* The mark is authored dark; inverted here for the dark surface. */}
              <Logo className="brightness-0 invert" data-id="element-4425" data-mp="2te9frjlj25t3" />
              <p className="mt-4 max-w-[34ch] text-body-sm leading-6 text-white/70" data-id="element-4426" data-mp="2tfssj07z5yq4">
                The campus operating system for students, campuses and trusted providers.
              </p>
              <p className="mt-3 text-caption text-white/50" data-id="element-4427" data-mp="35tza5y5xs2r0">A Havazon Ecosystem venture.</p>
              <ul className="mt-5 flex gap-2" data-id="element-4428" data-mp="2rydp222tq33v">
                {SOCIAL.map(item => {
                const Icon = item.icon;
                return <li key={item.label} data-id="element-4429" data-mp="3joi4dmbq7zy8">
                      <Link to="/support" aria-label={item.label} className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/15 text-white/70 transition-colors duration-150 ease-out hover:border-white/40 hover:text-white" data-id="element-4430" data-mp="3jpjr4o23uavs">
                        <Icon aria-hidden className="h-5 w-5" data-id="element-4431" data-mp="6gotduk3gnrz" />
                      </Link>
                    </li>;
              })}
              </ul>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5" data-id="element-4432" data-mp="2jp98wlxhzmao">
              {FOOTER_COLUMNS.map(column => <div key={column.heading} data-id="element-4433" data-mp="2eolio2h3f9hi">
                  <h2 className="text-body-sm font-semibold text-white" data-id="element-4434" data-mp="2erpthip2d3h5">{column.heading}</h2>
                  <ul className="mt-3 space-y-2" data-id="element-4435" data-mp="29smq02oqf5ba">
                    {column.links.map((link, index) => <li key={`${column.heading}-${link.label}-${index}`} data-id="element-4436" data-mp="25rbaipzko0gt" data-mpi={index}>
                        <Link to={link.to} className="text-body-sm text-white/70 transition-colors duration-150 ease-out hover:text-white" data-id="element-4437" data-mp="32efv4ixi2w18">
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          <ul className="mt-10 grid gap-2 border-t border-white/10 pt-6 text-caption text-white/60 sm:grid-cols-2" data-id="element-4438" data-mp="3gf0fhjj2czwu">
            <li data-id="element-4439" data-mp="12wmbq2jqfuxd">Campuzon Verified means an authorised representative inspected the listing.</li>
            <li data-id="element-4440" data-mp="17v5uxv6xysm1">Campuzon never asks for your university password.</li>
          </ul>
        </div>

        <div className="border-t border-white/10" data-id="element-4441" data-mp="1bxalum609b4z">
          <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-4 text-caption text-white/60 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-4442" data-mp="1bvhhjdiv7305">
            <p data-id="element-4443" data-mp="1lvqgv085l4jr">
              © {new Date().getFullYear()} Campuzon Technologies. Payments and stored value operate
              through licensed providers where required.
            </p>
            <p data-id="element-4444" data-mp="1vla1aabil6gq">Built for campuses across Nigeria.</p>
          </div>
        </div>
      </footer>
    </div>;
}